import { z } from 'zod';

/**
 * F13 b2b-account-members types + Zod schemas.
 *
 * Decisions: see plans/13_F13_staff_permissions_and_trainer_affiliation.md
 * (Decisions 1, 3, 4, 14).
 *
 * ─── Role lifecycle ──────────────────────────────────────────────────────────
 *   owner | manager | front_desk | accountant
 *
 *   `owner` is implicit on B2B-account signup (F11 backfills one `role='owner'`
 *   row per pre-existing `b2b_accounts` row). Per Decision 14, exactly ONE
 *   active owner per account is enforced at the DB level via a partial unique
 *   index plus a service-layer count invariant on every owner-touching
 *   mutation. The only path to hand off ownership is the explicit OTP-gated
 *   `POST /me/b2b/account/transfer-ownership` endpoint.
 *
 * ─── Status lifecycle ────────────────────────────────────────────────────────
 *   active (default on accept) → suspended (owner override) → removed
 *
 *   A removed member can re-join via a fresh invite (the partial unique index
 *   `b2b_account_members_unique_active_per_user` lets `status='removed'` rows
 *   coexist with a new active row for the same user).
 *
 * ─── venueScope semantics ────────────────────────────────────────────────────
 *   NULL  = "all venues currently AND in the future under this account"
 *           (canonical Owner + chain-wide Manager case)
 *   [...] = "scoped strictly to these specific venue IDs"
 *
 *   Owners cannot have a non-NULL scope (CHECK constraint
 *   `b2b_account_members_owner_no_scope`). New venues added to the account
 *   automatically grant access to NULL-scope members but NOT to array-scope
 *   members — the owner must explicitly extend the array.
 *
 * ─── Permissions model ───────────────────────────────────────────────────────
 *   F13 ships fixed roles + pure role-based RBAC (Decision 1). The full
 *   role → permission map lives in `apps/api/src/lib/b2b-rbac.ts` (single
 *   source of truth, mirrors F06's `admin-rbac.ts` shape). This module only
 *   exports the type-level vocabulary — `B2bPermission` union — so apps,
 *   tests, and downstream packages can reference permission strings safely.
 *   The matrix itself is intentionally NOT frozen here to avoid duplicating
 *   the source of truth.
 */

// ─── Enum constants ──────────────────────────────────────────────────────────

// F36 — added `club_admin` + `club_trainer` (D3). These appear only on
// account_kind='club' accounts; the account `owner` doubles as the club owner
// (reusing F13's single-owner index + ownership-transfer verbatim).
export const B2B_ACCOUNT_MEMBER_ROLES = [
  'owner',
  'manager',
  'front_desk',
  'accountant',
  'club_admin',
  'club_trainer',
] as const;
export type B2bAccountMemberRole = (typeof B2B_ACCOUNT_MEMBER_ROLES)[number];

export const B2B_ACCOUNT_MEMBER_STATUSES = ['active', 'suspended', 'removed'] as const;
export type B2bAccountMemberStatus = (typeof B2B_ACCOUNT_MEMBER_STATUSES)[number];

/**
 * The full type-level vocabulary of B2B permissions emitted by the F13
 * role→permission matrix. The actual matrix assignments live in
 * `apps/api/src/lib/b2b-rbac.ts` (Wave 2, Task T3); types-only export here
 * lets shared code reference permission strings without depending on the API.
 *
 * Successor features add to this union:
 *   - F16 reservation engine added `bookings:cancel` (owner + manager only —
 *     unilateral cancel of an already-confirmed booking; front_desk gets
 *     accept/decline but not cancel) and `blockouts:write` (owner + manager +
 *     front_desk — write blockout rows on the same `bookings` table).
 *   - F33 trainer marketplace may add `trainer_packages:write` etc.
 *   - F38 subscription tiers may add `billing:read` / `billing:write`
 */
export const B2B_PERMISSIONS = [
  'staff:read',
  'staff:write',
  'account:read',
  'account:write',
  'venue_profile:read',
  'venue_profile:write',
  'bookings:read',
  'bookings:accept',
  'bookings:decline',
  'bookings:complete',
  'bookings:refund',
  'bookings:cancel',
  // F17 — booking lifecycle additions (Decision 13 of the F17 plan).
  // `bookings:no_show`           — owner/manager/front_desk: mark a confirmed
  //                                booking as no-show (manual action only;
  //                                no auto-no-show at MVP).
  // `bookings:reschedule_venue`  — owner/manager: move a confirmed booking
  //                                to a different slot. Front-desk cannot
  //                                unilaterally reschedule (matches
  //                                bookings:cancel scope).
  // `bookings:edit_cancellation_policy` — owner only: edit the per-venue
  //                                cancellation_policy jsonb override.
  // `bookings:mark_payment`      — RESERVED for F20 (owner/manager/
  //                                front_desk/accountant); F17 registers but
  //                                does not wire to any route.
  'bookings:no_show',
  'bookings:reschedule_venue',
  'bookings:edit_cancellation_policy',
  'bookings:mark_payment',
  // F18 — walk-in tracking. F16 reserved a single walk-in permission
  // (deprecated); F18 SPLITS it into three with finer-grained venue-staff
  // scoping (Decision 5 — Front Desk creates but cannot edit / cancel;
  // Owner + Manager can do all three). Accountant has none.
  'bookings:walkin_create',
  'bookings:walkin_edit',
  'bookings:walkin_cancel',
  'blockouts:write',
  'pricing:read',
  'pricing:write',
  'accounting:read',
  'accounting:write',
  'payouts:read',
  'refunds:write',
  'analytics:read',
  'affiliations:read',
  'affiliations:write',
  'audit_log:read',
  // F36 — club operational permissions (D3). Granted on club accounts only:
  //   owner        → all club perms (+ existing account/staff/audit)
  //   club_admin   → club:* + club_teams:* + club_trainer:assign + staff:* +
  //                  audit_log:read + friendly:read + tournament:register_interest
  //                  (org management; NOT training/attendance/notes)
  //   club_trainer → club:read + club_teams:read + training:* + attendance:* +
  //                  friendly:* + tournament:register_interest + player_notes:write
  //                  (team operations, scoped to assigned teams via team_trainers)
  'club:read',
  'club:write',
  'club_teams:read',
  'club_teams:write',
  'club_trainer:assign',
  'training:read',
  'training:write',
  'attendance:read',
  'attendance:mark',
  'friendly:read',
  'friendly:manage',
  'tournament:register_interest',
  'player_notes:write',
  // F39 — Featured Placements (paid ad layer). Venue staff buy + view paid
  // placements for their venue. Granted (in b2b-rbac.ts):
  //   placements:read  → owner + manager + front_desk + accountant
  //   placements:write → owner + manager
  // Mirrors the bookings:*/accounting:* coarse pattern.
  'placements:read',
  'placements:write',
  // F40 — Promotions & Discount Codes (paid promo layer). Venue staff create +
  // view promotions for their venue. Granted (in b2b-rbac.ts):
  //   promotions:read  → owner + manager + accountant
  //   promotions:write → owner + manager
  // Front-desk gets neither (mirrors the placements:* + accounting:* split).
  'promotions:read',
  'promotions:write',
  // F41 — Tournament & Event Listings. Two distinct authZ paths use these:
  //   (a) ORGANIZER-side perms — gated by `hasTournamentPermission(actor,
  //       tournamentId, perm)` which resolves the actor's membership of the
  //       tournament's `organizer_b2b_account_id` (the `hasVenuePermission`
  //       pattern). Granted (in b2b-rbac.ts):
  //         tournaments:read           → owner + manager + club_admin + front_desk + accountant
  //         tournaments:write          → owner + manager + club_admin
  //         tournaments:mark_payment   → owner + manager + front_desk
  //         tournaments:announce       → owner + manager
  //         tournaments:manage_bracket → owner + manager
  //         tournaments:enter_scores   → owner + manager
  //   (b) VENUE-side perm — `tournaments:host_approve` is the venue owner /
  //       manager approving a hosting request, gated by F13's
  //       `hasVenuePermission(actor, venueId, 'tournaments:host_approve')`
  //       against the HOSTING VENUE's account (NOT the organizer's). Granted
  //       (in b2b-rbac.ts): owner + manager.
  'tournaments:read',
  'tournaments:write',
  'tournaments:mark_payment',
  'tournaments:announce',
  'tournaments:manage_bracket',
  'tournaments:enter_scores',
  'tournaments:host_approve',
  // F09 v2 (E18) — file a blue-tick request for a B2B-owned entity (venue /
  // club / organizer). OWNER ONLY (registered in `OWNER_PERMISSIONS` in
  // b2b-rbac.ts) — staff roles never hold it.
  'verification:request',
] as const;
export type B2bPermission = (typeof B2B_PERMISSIONS)[number];

// ─── Raw DB row shape (mirrors packages/db/src/schema.ts b2bAccountMembers) ──

/**
 * One row per (account, user) membership. The DB row carries audit columns and
 * lifecycle timestamps; the API serialises them as ISO strings.
 */
export type B2bAccountMember = {
  readonly id: string;
  readonly b2bAccountId: string;
  readonly userId: string;
  readonly role: B2bAccountMemberRole;
  readonly venueScope: ReadonlyArray<string> | null;
  readonly status: B2bAccountMemberStatus;
  readonly invitedByUserId: string | null;
  readonly invitedViaInviteId: string | null;
  readonly acceptedAt: string | null;
  readonly suspendedAt: string | null;
  readonly removedAt: string | null;
  readonly countryCode: string;
  readonly createdAt: string;
  readonly updatedAt: string;
};

/**
 * `GET /me/b2b/account/members` row (N40): the membership plus the member's
 * identity for the staff list (staff-managing viewers only — owner, manager,
 * club_admin). `user` is null only if the user row is gone.
 */
export type B2bAccountMemberWithUser = B2bAccountMember & {
  readonly user: {
    readonly id: string;
    readonly displayName: string | null;
    readonly email: string;
    readonly avatarUrl: string | null;
  } | null;
};

/**
 * Shape consumed by `<PermissionMatrix>` in the staff detail page. Maps each
 * role to the set of permissions it grants. The concrete data lives in
 * `apps/api/src/lib/b2b-rbac.ts`; this is a type-only contract surface.
 */
export type B2bAccountMemberPermissions = {
  readonly [Role in B2bAccountMemberRole]: ReadonlyArray<B2bPermission>;
};

/**
 * Response shape for `GET /me/venues/:venueId/me-permissions` — resolves the
 * caller's effective permissions at a specific venue.
 *
 * Two grant paths converge into `permissions`:
 *   - `b2b_account_members` row at the venue's owning account (role-based)
 *   - `venue_trainer_affiliations` row at the venue (affiliation-based)
 *
 * Field semantics:
 *   - `role`            — the caller's B2B role at this venue, or `null` if
 *                         they are not a staff member (e.g. affiliation-only,
 *                         or no relationship at all).
 *   - `venueScope`      — `null` = chain-wide (NULL membership scope OR no
 *                         membership at all); array = explicit scoped list
 *                         covering this venue.
 *   - `affiliationActive` — `true` iff the caller has an ACTIVE trainer
 *                         affiliation row at this venue (F33).
 *   - `permissions`     — union of role + affiliation permissions (the same
 *                         set returned by `getEffectivePermissions()` on the
 *                         server). Always a stable, sorted array so 60s
 *                         client cache keys are stable.
 */
export type VenuePermissionsResponse = {
  readonly venueId: string;
  readonly userId: string;
  readonly role: B2bAccountMemberRole | null;
  readonly venueScope: ReadonlyArray<string> | null;
  readonly affiliationActive: boolean;
  readonly permissions: ReadonlyArray<B2bPermission>;
};

// ─── Zod schemas ─────────────────────────────────────────────────────────────

export const B2bAccountMemberRoleSchema = z.enum(B2B_ACCOUNT_MEMBER_ROLES);
export const B2bAccountMemberStatusSchema = z.enum(B2B_ACCOUNT_MEMBER_STATUSES);
export const B2bPermissionSchema = z.enum(B2B_PERMISSIONS);

/**
 * Role enum for write paths — `owner` is intentionally excluded. An owner is
 * only ever set via account creation (F11) or the OTP-gated ownership-transfer
 * flow (Decision 14). The staff-invite + role-change endpoints reject
 * `'owner'` at the schema level.
 */
const InvitableMemberRoleSchema = z.enum(['manager', 'front_desk', 'accountant']);

/**
 * `venueScope` is a non-empty list of UUIDs when present. NULL signals
 * "all venues under this account" (chain-wide).
 */
const VenueScopeSchema = z.array(z.string().uuid()).nullable();

// ─── Input schemas ───────────────────────────────────────────────────────────

export const InviteStaffInputSchema = z
  .object({
    inviteeEmail: z.string().email(),
    role: InvitableMemberRoleSchema,
    venueScope: VenueScopeSchema,
  })
  .strict();
export type InviteStaffInput = z.infer<typeof InviteStaffInputSchema>;

export const ChangeMemberRoleInputSchema = z
  .object({
    newRole: InvitableMemberRoleSchema,
  })
  .strict();
export type ChangeMemberRoleInput = z.infer<typeof ChangeMemberRoleInputSchema>;

export const ChangeMemberScopeInputSchema = z
  .object({
    venueScope: VenueScopeSchema,
  })
  .strict();
export type ChangeMemberScopeInput = z.infer<typeof ChangeMemberScopeInputSchema>;

export const SuspendMemberInputSchema = z
  .object({
    reason: z.string().max(500).optional(),
  })
  .strict();
export type SuspendMemberInput = z.infer<typeof SuspendMemberInputSchema>;

export const RemoveMemberInputSchema = z
  .object({
    reason: z.string().max(500).optional(),
  })
  .strict();
export type RemoveMemberInput = z.infer<typeof RemoveMemberInputSchema>;

/**
 * First step of the OTP-gated ownership-transfer Stepper flow (Decision 14).
 * The current owner picks the new owner; the API issues an OTP via F07's
 * OTP infra with `purpose='b2b_owner_transfer'` and emails the current owner.
 */
export const TransferOwnershipInitiateInputSchema = z
  .object({
    newOwnerUserId: z.string().uuid(),
  })
  .strict();
export type TransferOwnershipInitiateInput = z.infer<typeof TransferOwnershipInitiateInputSchema>;

/**
 * Final step of the OTP-gated ownership-transfer flow. The 6-digit OTP is
 * matched against the previously issued challenge; on success the API
 * atomically demotes the outgoing owner to `manager` and promotes the new
 * owner to `owner` inside a single transaction.
 */
export const TransferOwnershipConfirmInputSchema = z
  .object({
    otpCode: z.string().regex(/^\d{6}$/),
  })
  .strict();
export type TransferOwnershipConfirmInput = z.infer<typeof TransferOwnershipConfirmInputSchema>;
